import React, { useRef, useState } from "react";
import axios from "axios";
import { Mic, Square, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reusable voice input: record → Whisper transcribe → onTranscript(text).
export default function MicButton({ onTranscript, disabled = false, size = 40, testId = "mic-btn" }) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1000) {
          setState("idle");
          return;
        }
        setState("transcribing");
        try {
          const form = new FormData();
          form.append("audio", blob, "voice.webm");
          const { data } = await axios.post(`${API}/stt/transcribe`, form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
          if (data.text) onTranscript(data.text);
          else setError("We couldn't hear anything — try again a little closer to the mic.");
        } catch (err) {
          console.error("Transcription failed", err);
          setError(err.response?.data?.detail || "Couldn't transcribe — please try again.");
        } finally {
          setState("idle");
        }
      };
      recRef.current = rec;
      rec.start();
      setState("recording");
    } catch (err) {
      console.error("Mic access failed", err);
      setError("Microphone unavailable — check your browser permissions.");
    }
  };

  const stop = () => {
    recRef.current?.stop();
  };

  const recording = state === "recording";
  const transcribing = state === "transcribing";

  return (
    <div className="inline-flex flex-col items-center">
      <button
        type="button"
        onClick={recording ? stop : start}
        disabled={disabled || transcribing}
        className="inline-flex items-center justify-center transition-all flex-shrink-0"
        style={{
          width: size,
          height: size,
          borderRadius: "2px",
          border: `1px solid ${recording ? "var(--iw-accent-red)" : "var(--iw-border)"}`,
          background: recording ? "var(--iw-accent-red)" : "#fff",
          color: recording ? "#fff" : "var(--iw-primary)",
          animation: recording ? "iw-mic-pulse 1.4s ease-in-out infinite" : "none",
          cursor: disabled || transcribing ? "not-allowed" : "pointer",
        }}
        aria-label={recording ? "Stop recording" : "Record a voice message"}
        title={recording ? "Stop recording" : "Speak instead of typing"}
        data-testid={testId}
      >
        {transcribing ? (
          <Loader2 size={size * 0.45} className="animate-spin" aria-hidden="true" />
        ) : recording ? (
          <Square size={size * 0.4} fill="currentColor" aria-hidden="true" />
        ) : (
          <Mic size={size * 0.45} aria-hidden="true" />
        )}
      </button>
      {error && (
        <span
          className="text-xs mt-1 max-w-[180px] text-center leading-tight"
          style={{ color: "var(--iw-accent-red)" }}
          data-testid={`${testId}-error`}
        >
          {error}
        </span>
      )}
      <style>{`@keyframes iw-mic-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(200,50,50,0.35);} 50% { box-shadow: 0 0 0 8px rgba(200,50,50,0);} }`}</style>
    </div>
  );
}
